// src/features/auth/hooks/useAuth.ts
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { login, logout, refreshToken } from '@/api/auth';
import type { LoginRequestDTO, LoginResponseDTO } from '@/types';
import { useState, useEffect, useCallback } from 'react';
import { setAuthState as setTokenAuthState } from '@/api/utils/tokenRefreshContext';

// Interface for localStorage auth data (uses ISO string for expiresAt)
interface LocalStorageAuthData {
  accessToken: string;
  refreshToken: string;
  expiresAt: string; // ISO string timestamp
  user: any;
}

/**
 * Hook that provides login, logout and token refresh capabilities.
 *
 * **Security note**: Tokens are persisted in `localStorage` for simplicity.
 * In a production environment this is vulnerable to XSS attacks. Prefer
 * using `httpOnly` secure cookies set by the backend, or a more secure
 * client‑side storage mechanism. The implementation stores the full
 * `LoginResponseDTO` but you may want to limit the persisted fields to the
 * access token only.
 */
export const useAuth = () => {
  const queryClient = useQueryClient();

  // Reactive auth state that reflects the persisted storage.
  const [authState, setAuthState] = useState<Partial<LoginResponseDTO> | null>(null);

  // Track login success state for redirect handling
  const [loginSuccess, setLoginSuccess] = useState<boolean>(false);

  // Helper to persist auth data safely and keep the React state in sync.
  // CRITICAL: Also updates tokenRefreshContext so the API client can access the tokens
  const persistAuth = useCallback((data: LoginResponseDTO) => {
    try {
      const { accessToken, refreshToken, expiresIn, user } = data;
      
      // Calculate absolute expiry timestamp (from current time)
      const expiresAt = new Date(Date.now() + expiresIn * 1000);
      
      // Store in localStorage with absolute expiresAt timestamp
      const localStorageData: LocalStorageAuthData = {
        accessToken,
        refreshToken,
        expiresAt: expiresAt.toISOString(),
        user,
      };
      localStorage.setItem('auth', JSON.stringify(localStorageData));
      
      // Update React state for UI updates
      setAuthState({ accessToken, refreshToken, expiresIn, user });
      
      // CRITICAL: Update tokenRefreshContext so API client interceptor can access tokens
      setTokenAuthState({
        accessToken,
        refreshToken,
        expiresAt,
        user,
        isAuthenticated: true,
      });
      
      setLoginSuccess(true); // Mark login as successful
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist auth data', e);
    }
  }, []);

  // Initialise auth state from storage on mount (including SSR guard).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem('auth');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as LocalStorageAuthData;
      
      // Parse the absolute expiresAt timestamp
      const expiresAt = new Date(parsed.expiresAt);
      
      // Check if token is still valid
      const isTokenValid = expiresAt.getTime() > Date.now();
      
      if (isTokenValid) {
        setAuthState({
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
          expiresIn: Math.floor((expiresAt.getTime() - Date.now()) / 1000),
          user: parsed.user,
        });
        
        // Initialize tokenRefreshContext with the parsed data
        setTokenAuthState({
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
          expiresAt,
          user: parsed.user,
          isAuthenticated: true,
        });
        setLoginSuccess(true);
      } else {
        // Token expired, clear everything
        localStorage.removeItem('auth');
        setAuthState(null);
        setTokenAuthState(null);
        setLoginSuccess(false);
      }
    } catch (e) {
      console.error('Failed to parse auth data from localStorage', e);
      // ignore malformed data
      localStorage.removeItem('auth');
      setAuthState(null);
      setTokenAuthState(null);
      setLoginSuccess(false);
    }
    
    // Listen for changes from other tabs/windows.
    const handler = (e: StorageEvent) => {
      if (e.key === 'auth') {
        if (e.newValue) {
          try {
            const parsed = JSON.parse(e.newValue) as LocalStorageAuthData;
            const expiresAt = new Date(parsed.expiresAt);
            const isTokenValid = expiresAt.getTime() > Date.now();
            
            if (isTokenValid) {
              setAuthState({
                accessToken: parsed.accessToken,
                refreshToken: parsed.refreshToken,
                expiresIn: Math.floor((expiresAt.getTime() - Date.now()) / 1000),
                user: parsed.user,
              });
              setTokenAuthState({
                accessToken: parsed.accessToken,
                refreshToken: parsed.refreshToken,
                expiresAt,
                user: parsed.user,
                isAuthenticated: true,
              });
              setLoginSuccess(true);
            } else {
              setAuthState(null);
              setTokenAuthState(null);
              setLoginSuccess(false);
            }
          } catch {
            setAuthState(null);
            setTokenAuthState(null);
            setLoginSuccess(false);
          }
        } else {
          setAuthState(null);
          setTokenAuthState(null);
          setLoginSuccess(false);
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const loginMutation = useMutation((payload: LoginRequestDTO) => login(payload), {
    onSuccess: (data: LoginResponseDTO) => {
      persistAuth(data);
      // Invalidate any auth‑protected queries – you can scope this to a key if desired
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      // Clear login success state on error
      setLoginSuccess(false);
    },
  });

  const logoutMutation = useMutation(() => logout(), {
    onSuccess: () => {
      localStorage.removeItem('auth');
      setAuthState(null);
      setTokenAuthState(null); // Clear tokenRefreshContext
      setLoginSuccess(false);
      // Clear the entire query cache to remove any stale, protected data
      queryClient.clear();
    },
  });

  const refreshMutation = useMutation((refresh: string) => refreshToken(refresh), {
    onSuccess: (data: LoginResponseDTO) => {
      persistAuth(data);
    },
  });

  return {
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    refresh: refreshMutation.mutateAsync,
    authState,
    loginSuccess, // Expose login success state
    isLoading: loginMutation.isLoading || logoutMutation.isLoading || refreshMutation.isLoading,
    error: loginMutation.error || logoutMutation.error || refreshMutation.error,
    resetLoginSuccess: () => setLoginSuccess(false), // Allow resetting the success flag
  };
};